import {useContext, useEffect, useState} from "react";
import {useRecoilValue} from "recoil";
import {ilJsLoadedState, memberState} from "../../state/atoms";
import {AppContext} from "../../AppContext";
import './MyUploads.scss';
import {Spinner} from "../account/misc/Spinner";
import {Button} from "../account/misc/Button";
import {env} from "../../env/env";
import MembersApi from "../../api/MembersApi";

export enum Tab { SelectFromUploads, SelectFromPos }

interface Props {
  preselectedTab?: Tab;
  onClose?: () => void;
  onSelect?: (f: File) => void;
}

export default function MyUploads({preselectedTab, onClose, onSelect}: Props) {
  const [memberImagesLoading, setMemberImagesLoading] = useState(true);
  const [memberImages, setMemberImages] = useState([] as MemberImageDto[]);
  const [posImagesLoading, setPosImagesLoading] = useState(true);
  const [posImages, setPosImages] = useState([] as ILMenuItemImageDto[]);
  const {location} = useContext(AppContext);
  const ilJsLoaded = useRecoilValue(ilJsLoadedState);
  const member = useRecoilValue(memberState);
  const [tab, setTab] = useState(preselectedTab || Tab.SelectFromUploads);
  const [selectedImage, setSelectedImage] = useState(null as MemberImageDto | null);
  const [selectedPosImage, setSelectedPosImage] = useState(null as ILMenuItemImageDto | null);

  useEffect(() => {
    setTab(preselectedTab || Tab.SelectFromUploads);
  }, [preselectedTab]);

  useEffect(() => {
    if (!location || !location.internalId || !member || !member.internalId || !ilJsLoaded) return;
    const props = {memberId: member.internalId, locationId: location.internalId};
    setMemberImagesLoading(true);
    window.ItemLibrary.Lib.getMemberImages(props).then(imgs => {
      setMemberImagesLoading(false);
      setMemberImages(imgs);
    });
    setPosImagesLoading(true);
    window.ItemLibrary.Lib.getPOSImages(props).then(imgs => {
      setPosImagesLoading(false);
      setPosImages(imgs);
    });
  }, [location, member, ilJsLoaded]);

  function onMemberImageClick(img: MemberImageDto) {
    setSelectedImage(img);
    setSelectedPosImage(null);
  }

  function onPosImageClick(img: ILMenuItemImageDto) {
    setSelectedPosImage(img);
    setSelectedImage(null);
  }

  function getImagePath(img: MemberImageDto) {
    const key = img.path!;
    const isOONative = img.source === 'oo';
    const s3 = isOONative ? env.oos3Path : env.s3Path;
    return key.indexOf('https') > -1 ? key : `${s3}/${key}`;
  }

  async function onAddImage() {
    if (selectedPosImage) {
      onSelect?.(await urlToFile(selectedPosImage.url!, selectedPosImage.memberImageId));
    } else {
      onSelect?.(await urlToFile(getImagePath(selectedImage!), selectedImage!.name));
    }
    setSelectedImage(null);
    setSelectedPosImage(null);
    onClose?.();
  }

  function onCloseClick() {
    setSelectedImage(null);
    setSelectedPosImage(null);
    onClose?.();
  }

  function base64ToBlob(base64: string, contentType: string = 'image/png'): Blob {
    try {
      const base64Data = base64.replace(/^data:image\/\w+;base64,/, '');
      const byteString = atob(base64Data);
      const byteArray = new Uint8Array(byteString.length);

      for (let i = 0; i < byteString.length; i++) {
        byteArray[i] = byteString.charCodeAt(i);
      }

      return new Blob([byteArray], { type: contentType });
    } catch (error) {
      console.error(error);
    }
    throw new Error('Failed to convert');
  }

  async function urlToFile(url: string, filename = 'image') {
    try {
      const response = await MembersApi.downloadImg(url);
      const mimeType = response.data.mimeType || 'image/jpeg';

      const extension = filename.includes('.') ? '' : '.' + (mimeType.split('/')[1] || 'jpg');

      return new File([base64ToBlob(response.data.base64!, response.data.mimeType!)], `${filename}${extension}`, {type: mimeType});
    } catch (error) {
      console.error('Error converting URL to File:', error);
      throw error;
    }
  }

  return <div className={'my-uploads-modal'}>
    <header>
      <div className={'tabs'}>
        <div className={tab === Tab.SelectFromUploads ? 'active' : ''} onClick={() => setTab(Tab.SelectFromUploads)}>My Images</div>
        <div className={tab === Tab.SelectFromPos ? 'active' : ''} onClick={() => setTab(Tab.SelectFromPos)}>My POS Images</div>
      </div>
    </header>

    {((tab === Tab.SelectFromUploads && memberImagesLoading) || (tab === Tab.SelectFromPos && posImagesLoading)) && <div className={'spinner-container'}><Spinner /></div>}
    {!((tab === Tab.SelectFromUploads && memberImagesLoading) || (tab === Tab.SelectFromPos && posImagesLoading)) && <section>
      {tab === Tab.SelectFromUploads && (
        <>
          {!memberImagesLoading && !memberImages.length && <span className={'noSavedImages'}>No saved images yet!</span>}

          {!!memberImages.length &&
            <div>
              {memberImages.map((img) => (
                <div key={img.id}>
                  {selectedImage?.id === img.id && <input type={'checkbox'} checked={true} onChange={() => {
                  }}/>}
                  <img src={getImagePath(img)} alt={img.altText} loading={'lazy'} onClick={() => onMemberImageClick(img)}/>
                </div>
              ))}
            </div>
          }
        </>
      )}
      {tab === Tab.SelectFromPos && (
        <>
          {!posImagesLoading && !posImages.length && <span className={'noSavedImages'}>No saved images yet!</span>}
          {!!posImages.length &&
            <div>
              {posImages.map((img) => (
                <div key={img.id}>
                  {selectedPosImage?.id === img.id && <input type={'checkbox'} checked={true} onChange={() => {
                  }}/>}
                  <img src={img.url} alt={''} loading={'lazy'} onClick={() => onPosImageClick(img)}/>
                </div>
              ))}
            </div>
          }
        </>
      )}
    </section>}
    <footer>
      <Button type={'Secondary'} onClick={onCloseClick}>Cancel</Button>
      {(tab === Tab.SelectFromUploads || tab === Tab.SelectFromPos) && <Button type={'Primary'} onClick={onAddImage} disabled={!selectedImage && !selectedPosImage}>Add Image</Button>}
    </footer>
  </div>
}
